import React from "react";
import { Search } from "lucide-react";
import { Button } from "../ui/Button";

export interface SunbedReportItem {
  id: string;
  customer: string;
  package: string;
  mins: string;
  amount: string;
  gateway: string;
  rechargedDate: string;
}

interface SunbedReportTableProps {
  reports: SunbedReportItem[];
  isLoading: boolean;
  indexOfFirstItem: number;
}

export const SunbedReportTable: React.FC<SunbedReportTableProps> = ({
  reports,
  isLoading,
  indexOfFirstItem,
}) => {
  return (
    <div className="flex-1 lg:overflow-auto relative custom-scrollbar rounded-xl border-0 lg:border border-gray-200">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold whitespace-nowrap">#</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Customer</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Sunbed Package</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Sunbed Mins</th>
            <th className="col-span-1 text-left font-bold whitespace-nowrap">Amount</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Gateway</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Recharged Date</th>
          </tr>
        </thead>
        <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
          {isLoading ? (
            <tr className="h-64 w-full flex items-center justify-center lg:table-row">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : reports.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
              <td colSpan={12} className="w-full h-full p-0">
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <Search className="w-12 h-12 mb-3 opacity-20" />
                  <p className="text-lg font-medium text-gray-500">
                    No reports found
                  </p>
                  <p className="text-sm">Try adjusting your search terms</p>
                </div>
              </td>
            </tr>
          ) : (
            <>
              {reports.map((report, idx) => (
                <tr
                  key={report.id}
                  className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-4 items-stretch lg:items-center px-5 py-4 lg:px-6 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full"
                >
                  <td className="lg:col-span-1 text-gray-400 font-bold lg:font-medium flex justify-between items-center lg:block">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Record #</span>
                    <span>{indexOfFirstItem + idx + 1}</span>
                  </td>

                  <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Customer</span>
                    <span className="truncate">{report.customer}</span>
                  </td>

                  <td className="lg:col-span-2 font-medium text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Package</span>
                    <span className="truncate font-semibold lg:font-normal">{report.package}</span>
                  </td>

                  <td className="lg:col-span-2 font-medium text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Minutes</span>
                    <span className="font-bold lg:font-normal">{report.mins}</span>
                  </td>

                  <td className="lg:col-span-1 font-bold text-[#885ABB] lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Amount</span>
                    <span className="text-base lg:text-sm">{report.amount}</span>
                  </td>

                  <td className="lg:col-span-2 text-gray-600 font-medium lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Gateway</span>
                    <span className="truncate">{report.gateway}</span>
                  </td>

                  <td className="lg:col-span-2 text-gray-500 font-medium lg:text-gray-700 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Date</span>
                    <span className="truncate text-[11px] lg:text-sm">{report.rechargedDate}</span>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
